// src/pages/DocumentDetail.jsx
// Mirrors design.asp — shows a document's fields in view or edit mode.
// Also used for new documents (isNew=true) mirroring newRec.asp.

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import FieldRenderer from '../components/FieldRenderer';
import ConfirmModal from '../components/modals/ConfirmModal';
import SelectKeyModal from '../components/modals/SelectKeyModal';
import TabFiles from '../components/tabs/TabFiles';
import TabBlog from '../components/tabs/TabBlog';
import TabDescriptors from '../components/tabs/TabDescriptors';
import { documentApi, documentsApi, tabsApi } from '../services/api';
import { useTexts, TXT } from '../context/TextContext';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

export default function DocumentDetail() {
  const { id }            = useParams();
  const [searchParams]    = useSearchParams();
  const navigate          = useNavigate();
  const location          = useLocation();
  const isNew             = location.pathname.endsWith('/new') || id === 'new';
  const fromFilter        = location.state || {};
  const { t } = useTexts();
  const { showToast } = useToast();
  const { user } = useAuth();
  const canMagicLink = (user?.functions || []).includes(35);

  const [downloadingAll, setDownloadingAll] = useState(false);
  async function handleDownloadAll(e) {
    // Este botón vive dentro del <button> del header del acordeón (ver
    // AccordionTab). El toggle de Bootstrap se resuelve en un listener de
    // CAPTURA sobre document (event.target.closest('[data-bs-toggle]')),
    // así que ya abre/cierra el panel antes de que este handler llegue a
    // correr -- no hay forma de evitarlo desde acá. Lo compensamos
    // re-clickeando el toggle para volver al estado previo, pero recién
    // cuando termina la animación de Bootstrap (shown/hidden.bs.collapse):
    // clickear de nuevo mientras todavía está animando no hace nada,
    // Bootstrap ignora los toggles mientras hay una transición en curso.
    const toggleBtn = e?.currentTarget?.closest('.accordion-button');
    const targetSel = toggleBtn?.getAttribute('data-bs-target');
    const collapseEl = targetSel && document.querySelector(targetSel);
    if (toggleBtn && collapseEl) {
      // aria-expanded ya refleja el estado POST-toggle (el capture ya
      // corrió) -- si ahora dice "false" es porque el click lo cerró
      // (estaba abierto), y viceversa.
      const justClosed = toggleBtn.getAttribute('aria-expanded') === 'false';
      collapseEl.addEventListener(
        justClosed ? 'hidden.bs.collapse' : 'shown.bs.collapse',
        () => toggleBtn.click(),
        { once: true }
      );
    }

    setDownloadingAll(true);
    try {
      const res = await tabsApi.getFiles(doc.docId);
      const files = res.data?.files || [];
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const token = localStorage.getItem('whl_token');
      for (const f of files) {
        const r = await fetch(`${API_URL}/tabs/files/${f.id}/download`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const blob = await r.blob();
        const cd = r.headers.get('Content-Disposition') || '';
        const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';\r\n]+)/i);
        const filename = match ? decodeURIComponent(match[1]) : f.name;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        await new Promise(r => setTimeout(r, 300));
      }
    } catch { showToast(t(TXT.TRANSACTION_ERROR, 'Error'), 'error'); }
    finally { setDownloadingAll(false); }
  }

  const [doc, setDoc]           = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [editMode, setEditMode] = useState(isNew);
  const [error, setError]       = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [tabVisible, setTabVisible] = useState({ files: true, blog: true, descrip: true });
  const hideTab = (tab) => setTabVisible(prev => ({ ...prev, [tab]: false }));
  const [openBlogDefault, setOpenBlogDefault] = useState(null); // null = pending check
  const [canCreate, setCanCreate] = useState(false);
  const [showSelectKey, setShowSelectKey] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [pendingNotify, setPendingNotify] = useState(false);

  // Magic link modal state
  const [showMagicLink, setShowMagicLink]       = useState(false);
  const [mlSearch, setMlSearch]                 = useState('');
  const [mlOptions, setMlOptions]               = useState([]);
  const [mlSelected, setMlSelected]             = useState(null);
  const [mlGenerating, setMlGenerating]         = useState(false);
  const mlSearchTimer                           = useRef(null);

  useEffect(() => {
    clearTimeout(mlSearchTimer.current);
    if (!mlSearch.trim()) { setMlOptions([]); return; }
    mlSearchTimer.current = setTimeout(() => {
      tabsApi.searchMentions(mlSearch)
        .then(res => setMlOptions((res.data || []).filter(o => o.tipo === 'U')))
        .catch(() => {});
    }, 250);
  }, [mlSearch]);

  // Track field value changes
  const changedValues = useRef({});

  useEffect(() => {
    loadDocument();
    documentsApi.canCreate().then(res => setCanCreate(res.data.canCreate)).catch(() => {});
  }, [id]);

  // Verificar menciones no leídas y marcarlas como leídas al abrir el documento
  useEffect(() => {
    if (id && id !== 'new') {
      tabsApi.unreadMentions(id)
        .then(res => {
          setOpenBlogDefault(res.data.count > 0);
          tabsApi.markMentionsRead(id).catch(() => {});
        })
        .catch(() => setOpenBlogDefault(false));
    }
  }, [id]);

  // Navigate back to list when a filter or search is triggered from navbar
  useEffect(() => {
    const handleFilter = (e) => navigate('/documents', { state: { filterId: e.detail?.filterId } });
    const handleSearch = (e) => navigate('/documents', { state: { search: e.detail?.search } });
    window.addEventListener('whl:filter', handleFilter);
    window.addEventListener('whl:search', handleSearch);
    return () => {
      window.removeEventListener('whl:filter', handleFilter);
      window.removeEventListener('whl:search', handleSearch);
    };
  }, [navigate]);

  const loadDocument = async () => {
    setLoading(true);
    setError(null);
    try {
      let res;
      if (isNew) {
        res = await documentApi.getNew({
          docTypeId: searchParams.get('docTypeId'),
          areaId:    searchParams.get('areaId'),
          privacyId: searchParams.get('privacyId'),
        });
        // Resolver defaults especiales antes de montar el formulario
        const today = new Date().toISOString().slice(0, 10);
        res.data.fields = res.data.fields.map(f => {
          if (f.defau?.toLowerCase() === 'getdate()' && (f.type === 'D' || f.type === 'H'))
            return { ...f, value: today };
          return f;
        });
      } else {
        res = await documentApi.get(id);
      }
      setDoc(res.data);
      changedValues.current = {};
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar el documento');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId, fieldName, value) => {
    changedValues.current[fieldId] = { id: fieldId, name: fieldName, value };
  };

  const handleSave = async () => {
    // Merge current field values with any changes
    const fields = doc.fields.map(f => ({
      id:       f.id,
      name:     f.name,
      type:     f.type,
      predef:   f.predef,
      required: f.required,
      value:    changedValues.current[f.id]?.value ?? f.value,
    }));

    // Validate required fields
    const missing = fields.filter(f => f.required && (f.value === null || f.value === undefined || String(f.value).trim() === ''));
    if (missing.length) {
      const labels = missing.map(f => f.label || f.name).join(', ');
      showToast(`${t(TXT.MANDATORY, 'Obligatorio')}: ${labels}`, 'error');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const res = await documentApi.create({
          docTypeId: doc.docTypeId,
          areaId:    doc.areaId,
          privacyId: doc.privacyId,
          fields,
        });
        showToast(t(TXT.DATA_SAVED, 'Datos guardados'), 'success');
        setEditMode(false);
        navigate(`/documents/${res.data.docId}`);
      } else {
        await documentApi.update(id, {
          areaId:    doc.areaId,
          privacyId: doc.privacyId,
          fields,
        });
        showToast(t(TXT.DATA_UPDATED, 'Datos modificados'), 'success');
        setEditMode(false);
        setPendingNotify(true);
        loadDocument();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await documentApi.delete(id);
      navigate('/documents', { state: fromFilter });
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al eliminar', 'error');
    }
  };

  const handleCancel = () => {
    if (isNew) {
      navigate('/documents', { state: fromFilter });
    } else {
      changedValues.current = {};
      setEditMode(false);
      loadDocument();
    }
  };

  // Button bar content passed to Layout
  const buttonBar = doc ? (
    <div className="d-flex gap-2">
      {editMode ? (
        <>
          <button className="button color1" onClick={handleSave} disabled={saving}>
            <i className="fa-solid fa-check fa-1x color1" />
            &nbsp;&nbsp;{saving ? '…' : t(TXT.SAVE, 'Guardar')}&nbsp;&nbsp;
          </button>
          <button className="button color1" onClick={handleCancel} disabled={saving}>
            <i className="fa-solid fa-xmark fa-1x color1" />
            &nbsp;&nbsp;{t(TXT.CANCEL, 'Cancelar')}&nbsp;&nbsp;
          </button>
        </>
      ) : (
        <>
          <button className="button color1" onClick={() => navigate('/documents', { state: fromFilter })}>
            <i className="fa-solid fa-arrow-left fa-1x color1" />
            &nbsp;&nbsp;{t(TXT.BACK, 'Volver')}&nbsp;&nbsp;
          </button>
          {canCreate && (
            <button className="button color1" onClick={() => setShowSelectKey(true)}>
              <i className="fa-solid fa-plus fa-1x color1" />
              &nbsp;&nbsp;{t(TXT.NEW, 'Nuevo')}&nbsp;&nbsp;
            </button>
          )}
          {doc.canEdit && (
            <button className="button color1" onClick={() => setEditMode(true)}>
              <i className="fa-solid fa-pen fa-1x color1" />
              &nbsp;&nbsp;{t(TXT.UPDATE, 'Modificar')}&nbsp;&nbsp;
            </button>
          )}
          {doc.canDelete && (
            <button className="button color1" onClick={() => setShowConfirmDelete(true)}>
              <i className="fa-solid fa-trash fa-1x color1" />
              &nbsp;&nbsp;{t(TXT.DELETE, 'Eliminar')}&nbsp;&nbsp;
            </button>
          )}
          {canMagicLink && !isNew && (
            <button className="button color1" onClick={() => { setMlSelected(null); setMlSearch(''); setShowMagicLink(true); }}>
              <i className="fa-solid fa-link fa-1x color1" />
              &nbsp;&nbsp;Link&nbsp;&nbsp;
            </button>
          )}
          {doc.canEdit && pendingNotify && (
            <button className="button color1" disabled={notifying} onClick={async () => {
              setNotifying(true);
              try {
                await documentApi.notify(id);
                showToast(t(TXT.NOTIFICATION_SENT, 'Notificación enviada'), 'success');
                setPendingNotify(false);
              } catch {
                showToast(t(TXT.TRANSACTION_ERROR, 'Error al notificar'), 'error');
              } finally {
                setNotifying(false);
              }
            }}>
              <i className="fa-solid fa-bell fa-1x color1" />
              &nbsp;&nbsp;{notifying ? '…' : t(TXT.NOTIFICATIONS, 'Notificar')}&nbsp;&nbsp;
            </button>
          )}
        </>
      )}
    </div>
  ) : null;

  if (loading) {
    return (
      <Layout>
        <div className="text-center mt-5">
          <div className="spinner-border text-secondary" role="status" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="alert alert-danger mt-3">{error}</div>
      </Layout>
    );
  }

  return (
    <Layout onNewDocument={null} summary={null} buttonBar={buttonBar}>

      <form id="FORM1" onSubmit={e => { e.preventDefault(); handleSave(); }}>
        <input type="hidden" name="iddoc"   value={doc.docId    || ''} readOnly />
        <input type="hidden" name="idtdo"   value={doc.docTypeId || ''} readOnly />

        {/* ---------------------------------------------------------------- */}
        {/* Key fields: Área and Estado — only shown in edit mode for existing docs */}
        {editMode && !isNew && (
        <div className="row mb-2">
          <div className="col-sm-6 mt-2">
            <div className="container">
              <div className="row campo" style={{ borderBottom: '1px solid' }}>
                <div className="col-sm-6 mb-2">{t(TXT.AREA_LABEL, 'Área:')}</div>
                <div className="col-sm-6 mb-2 valor">
                    <select
                      className="campo_fino"
                      style={{ width: '100%', border: 'none', outline: 'none' }}
                      defaultValue={doc.areaId}
                      onChange={e => setDoc(d => ({ ...d, areaId: parseInt(e.target.value) }))}
                    >
                      {(doc.areas || []).map(a => (
                        <option key={a.id} value={a.id}>{a.label}</option>
                      ))}
                    </select>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-6 mt-2">
            <div className="container">
              <div className="row campo" style={{ borderBottom: '1px solid' }}>
                <div className="col-sm-6 mb-2">{t(TXT.STATUS_LABEL, 'Estado:')}</div>
                <div className="col-sm-6 mb-2 valor">
                    <select
                      className="campo_fino"
                      style={{ width: '100%', border: 'none', outline: 'none' }}
                      defaultValue={doc.privacyId}
                      onChange={e => setDoc(d => ({ ...d, privacyId: parseInt(e.target.value) }))}
                    >
                      {(doc.privacies || []).map(p => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Dynamic fields */}
        <div className="row">
          {doc.fields.map((field, idx) => (
            <FieldRenderer
              key={field.id}
              field={field}
              docId={doc.docId}
              readOnly={!editMode || field.readOnly}
              onChange={handleFieldChange}
              onSignoff={loadDocument}
            />
          ))}
        </div>

      </form>

      {/* ---------------------------------------------------------------- */}
      {/* Accordion tabs: Files, Blog, Relations, Description */}
      {/* Mirrors the accordion-flush section at the bottom of design.asp  */}
      {/* ---------------------------------------------------------------- */}
      {!isNew && doc.tabs && (() => {
        const hasUnsignedSignoff = doc.fields.some(f => f.type === 'S' && !f.value);
        const showFiles = !hasUnsignedSignoff || doc.canBypassSignoff;
        if (openBlogDefault === null) return null;
        return (
        <div className="accordion accordion-flush mt-3" id="accordionDesign">
          {doc.tabs.relFiles && showFiles && tabVisible.files && (
            <AccordionTab id="tabFiles" title={t(TXT.FILES_TAB, 'Archivos adjuntos')} icon="bi-paperclip" defaultOpen={!openBlogDefault}
              action={
                <button className="btn btn-sm btn-light" title={t(TXT.DOWNLOAD_ALL, 'Descargar todo')}
                  disabled={downloadingAll}
                  onClick={handleDownloadAll}>
                  {downloadingAll
                    ? <span className="spinner-border spinner-border-sm" />
                    : <i className="fa-solid fa-download" />}
                </button>
              }>
              <TabFiles docId={doc.docId} onHide={() => hideTab('files')} />
            </AccordionTab>
          )}
          {doc.tabs.blog && tabVisible.blog && (
            <AccordionTab id="tabBlog" title={t(TXT.COMMENTS_TAB, 'Comentarios')} icon="bi-chat-text" defaultOpen={openBlogDefault}>
              <TabBlog docId={doc.docId} onHide={() => hideTab('blog')} />
            </AccordionTab>
          )}
          {doc.tabs.relCont && (
            <AccordionTab id="tabRelCont" title="Contenidos relacionados" icon="bi-link-45deg">
              <p className="text-muted fst-italic">Sin implementación en el sistema original.</p>
            </AccordionTab>
          )}
          {doc.tabs.descrip && tabVisible.descrip && (
            <AccordionTab id="tabDescrip" title={t(TXT.KEYWORDS_TAB, 'Descriptores')} icon="bi-tags">
              <TabDescriptors docId={doc.docId} onHide={() => hideTab('descrip')} />
            </AccordionTab>
          )}
        </div>
        );
      })()}

      {/* New document modal */}
      <SelectKeyModal
        show={showSelectKey}
        onClose={() => setShowSelectKey(false)}
        onConfirm={({ docTypeId, areaId, privacyId }) => {
          navigate(`/documents/new?docTypeId=${docTypeId}&areaId=${areaId}&privacyId=${privacyId}`);
        }}
      />

      {/* Delete confirmation */}
      <ConfirmModal
        show={showConfirmDelete}
        message={t(TXT.CONFIRM_DELETE, '¿Está seguro que desea eliminar este documento?')}
        onConfirm={handleDelete}
        onCancel={() => setShowConfirmDelete(false)}
      />

      {/* Magic link modal */}
      {showMagicLink && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 440 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="fa-solid fa-link me-2" />Generar link de acceso (7 días)</h5>
                <button type="button" className="btn-close" onClick={() => setShowMagicLink(false)} />
              </div>
              <div className="modal-body">
                <label className="form-label">Usuario destinatario:</label>

                {mlSelected ? (
                  <div className="d-flex align-items-center gap-2 p-2 border rounded">
                    <i className="fa-solid fa-user text-muted" />
                    <span className="flex-grow-1">{mlSelected.name}</span>
                    <i className="fa-solid fa-xmark" style={{ cursor: 'pointer', color: '#888' }}
                      onClick={() => setMlSelected(null)} />
                  </div>
                ) : (
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Buscar usuario…"
                      value={mlSearch}
                      onChange={e => setMlSearch(e.target.value)}
                      autoFocus
                    />
                    {mlOptions.length > 0 && (
                      <ul className="list-group position-absolute w-100 shadow" style={{ zIndex: 1000, top: '100%', maxHeight: 200, overflowY: 'auto' }}>
                        {mlOptions.map(o => (
                          <li key={o.id}
                            className="list-group-item list-group-item-action py-1"
                            style={{ cursor: 'pointer' }}
                            onClick={() => { setMlSelected(o); setMlSearch(''); setMlOptions([]); }}>
                            <i className="fa-solid fa-user me-2 text-muted" />{o.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="button" onClick={() => setShowMagicLink(false)}>{t(TXT.CANCEL, 'Cancelar')}</button>
                <button className="button color1" disabled={!mlSelected || mlGenerating}
                  onClick={async () => {
                    setMlGenerating(true);
                    try {
                      const res = await documentApi.magicLink(id, mlSelected.id, 7);
                      await navigator.clipboard.writeText(res.data.link);
                      showToast(t(TXT.LINK_COPIED, 'Link copiado al portapapeles'), 'success');
                      setShowMagicLink(false);
                    } catch (err) {
                      showToast(err.response?.data?.error || 'Error al generar el link', 'error');
                    } finally {
                      setMlGenerating(false);
                    }
                  }}>
                  {mlGenerating ? '…' : t(TXT.GENERATE_AND_COPY, 'Generar y copiar')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}

// Simple accordion tab component
function AccordionTab({ id, title, icon, children, defaultOpen = false, action }) {
  return (
    <div className="accordion-item border-0">
      <h2 className="accordion-header" id={`heading_${id}`} style={{ display: 'flex', alignItems: 'center' }}>
        <button
          className={`accordion-button text-white ${defaultOpen ? '' : 'collapsed'}`}
          style={{ backgroundColor: 'var(--bs-secondary, #6c757d)', flex: 1 }}
          type="button"
          data-bs-toggle="collapse"
          data-bs-target={`#collapse_${id}`}
          aria-expanded={defaultOpen ? 'true' : 'false'}
          aria-controls={`collapse_${id}`}
        >
          <i className={`bi ${icon} me-2`} />
          {title}
          {action && <span className="ms-3" onClick={e => e.stopPropagation()}>{action}</span>}
        </button>
      </h2>
      <div
        id={`collapse_${id}`}
        className={`accordion-collapse collapse ${defaultOpen ? 'show' : ''}`}
        aria-labelledby={`heading_${id}`}
        data-bs-parent="#accordionDesign"
      >
        <div className="accordion-body">
          {children}
        </div>
      </div>
    </div>
  );
}
